import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CurrencyPipe, DatePipe, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { rxResource } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { BaseChartDirective } from 'ng2-charts';
import type { ChartData, ChartOptions } from 'chart.js';
import { ApiService } from '../core/api.service';
import { Customer, Estimate, Order } from '../core/models';

const DAY_MS = 24 * 60 * 60 * 1000;

export type FinanceTab = 'all' | 'jobs' | 'diy';
export type FinanceRange = '30d' | '90d' | 'ytd' | 'all';

export interface FinanceRow {
  customer: Customer;
  estimate: Estimate;
  manufacturingCost: number;
  additionalCosts: number;
  profit: number;
  profitPercent: number;
  hasCostData: boolean;
  paid: number;
  balance: number;
}

export interface DiyRow {
  order: Order;
  revenue: number;
  profit: number;
  profitPercent: number;
}

@Component({
  selector: 'app-finances-page',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterModule,
    CurrencyPipe,
    DatePipe,
    DecimalPipe,
    FormsModule,
    BaseChartDirective,
  ],
  templateUrl: './finances-page.component.html',
  styleUrls: ['./finances-page.component.scss'],
})
export class FinancesPageComponent {
  private readonly api = inject(ApiService);

  private readonly rowsResource = rxResource({
    stream: () => this.loadRows(),
  });
  readonly rows = this.rowsResource.value;
  readonly loading = this.rowsResource.isLoading;

  private readonly diyResource = rxResource({
    stream: () =>
      this.api.getOrders().pipe(catchError(() => of([] as Order[]))),
  });
  readonly diyLoading = this.diyResource.isLoading;
  readonly diyRows = computed<DiyRow[]>(() =>
    (this.diyResource.value() ?? []).map((order) => ({
      order,
      revenue: order.total,
      profit: order.total,
      profitPercent: 100,
    })),
  );

  readonly activeTab = signal<FinanceTab>('all');
  readonly tabs: { value: FinanceTab; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'jobs', label: 'Jobs' },
    { value: 'diy', label: 'DIY' },
  ];

  readonly range = signal<FinanceRange>('all');
  readonly ranges: { value: FinanceRange; label: string }[] = [
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' },
    { value: 'ytd', label: 'Year to date' },
    { value: 'all', label: 'All time' },
  ];
  readonly search = signal('');

  readonly rangeLabel = computed(
    () =>
      this.ranges.find((r) => r.value === this.range())?.label ?? 'All time',
  );

  selectTab(tab: FinanceTab) {
    this.activeTab.set(tab);
  }

  setRange(value: FinanceRange) {
    this.range.set(value);
  }

  setSearch(value: string) {
    this.search.set(value);
  }

  // ── Filtering ───────────────────────────────────────────────────────────────
  private readonly rangeStart = computed<number | null>(() => {
    const range = this.range();
    if (range === 'all') return null;
    const now = new Date();
    if (range === 'ytd') return new Date(now.getFullYear(), 0, 1).getTime();
    return now.getTime() - (range === '30d' ? 30 : 90) * DAY_MS;
  });

  private inRange(value?: string): boolean {
    const start = this.rangeStart();
    if (start === null) return true;
    const time = value ? new Date(value).getTime() : NaN;
    return !Number.isNaN(time) && time >= start;
  }

  private matchesSearch(values: (string | undefined)[]): boolean {
    const query = this.search().trim().toLowerCase();
    if (!query) return true;
    return values.some((v) => (v ?? '').toLowerCase().includes(query));
  }

  readonly filteredJobs = computed(() =>
    (this.rows() ?? []).filter(
      (r) =>
        this.inRange(r.estimate.createdAt || r.customer.createdAt) &&
        this.matchesSearch([r.customer.name, r.customer.email]),
    ),
  );

  readonly filteredDiy = computed(() =>
    this.diyRows().filter(
      (r) =>
        this.inRange(r.order.createdAt) &&
        this.matchesSearch([
          r.order.customerName,
          r.order.customerEmail,
          r.order.id,
        ]),
    ),
  );

  readonly diyRevenue = computed(() =>
    this.filteredDiy().reduce((s, r) => s + r.revenue, 0),
  );

  readonly scopeLoading = computed(() => {
    const tab = this.activeTab();
    if (tab === 'diy') return this.diyLoading();
    if (tab === 'jobs') return this.loading();
    return this.loading() || this.diyLoading();
  });

  // ── KPI cards (scoped to the active tab + filters) ──────────────────────────
  readonly kpis = computed(() => {
    const jobs = this.activeTab() === 'diy' ? [] : this.filteredJobs();
    const diy = this.activeTab() === 'jobs' ? [] : this.filteredDiy();

    const jobRevenue = jobs.reduce((s, r) => s + r.estimate.total, 0);
    const jobCosts = jobs.reduce(
      (s, r) => s + r.manufacturingCost + r.additionalCosts,
      0,
    );
    const jobPaid = jobs.reduce((s, r) => s + r.paid, 0);

    const diyRevenue = diy.reduce((s, r) => s + r.revenue, 0);
    const diyPaid = diy.reduce(
      (s, r) => s + (r.order.paymentStatus === 'paid' ? r.revenue : 0),
      0,
    );

    const revenue = jobRevenue + diyRevenue;
    const costs = jobCosts;
    const profit = revenue - costs;
    const paid = jobPaid + diyPaid;

    return {
      revenue,
      costs,
      profit,
      margin: revenue > 0 ? (profit / revenue) * 100 : 0,
      paid,
      outstanding: revenue - paid,
      unprofitable: jobs.filter((r) => r.profit < 0).length,
    };
  });

  // ── Trend chart (revenue vs profit, bucketed by month) ──────────────────────
  readonly trendChart = computed<ChartData<'line'>>(() => {
    const buckets = new Map<
      string,
      { ts: number; label: string; revenue: number; profit: number }
    >();

    const add = (
      value: string | undefined,
      revenue: number,
      profit: number,
    ) => {
      const month = this.monthBucket(value);
      if (!month) return;
      const bucket = buckets.get(month.key) ?? {
        ts: month.ts,
        label: month.label,
        revenue: 0,
        profit: 0,
      };
      bucket.revenue += revenue;
      bucket.profit += profit;
      buckets.set(month.key, bucket);
    };

    if (this.activeTab() !== 'diy') {
      for (const row of this.filteredJobs()) {
        add(
          row.estimate.createdAt || row.customer.createdAt,
          row.estimate.total,
          row.profit,
        );
      }
    }
    if (this.activeTab() !== 'jobs') {
      for (const row of this.filteredDiy()) {
        add(row.order.createdAt, row.revenue, row.profit);
      }
    }

    const sorted = [...buckets.values()].sort((a, b) => a.ts - b.ts);
    return {
      labels: sorted.map((b) => b.label),
      datasets: [
        {
          label: 'Revenue',
          data: sorted.map((b) => b.revenue),
          borderColor: '#6366f1',
          backgroundColor: 'rgba(99, 102, 241, 0.12)',
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
        {
          label: 'Profit',
          data: sorted.map((b) => b.profit),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.12)',
          fill: true,
          tension: 0.35,
          borderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  });

  readonly hasTrend = computed(
    () => (this.trendChart().labels?.length ?? 0) > 0,
  );

  readonly trendOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: true, position: 'bottom' } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true },
    },
  };

  // ── Expandable rows + profit note ───────────────────────────────────────────
  readonly expandedKey = signal<string | null>(null);
  draftNote = '';
  savingNote = signal(false);

  toggleRow(row: FinanceRow) {
    const key = row.customer.id;
    if (this.expandedKey() === key) {
      this.expandedKey.set(null);
      return;
    }
    this.expandedKey.set(key);
    this.draftNote = row.estimate.profitNote ?? '';
  }

  saveNote(row: FinanceRow) {
    this.savingNote.set(true);
    const updatedEstimate: Estimate = {
      ...row.estimate,
      profitNote: this.draftNote.trim() || undefined,
    };
    this.api.saveEstimate(row.customer.id, updatedEstimate).subscribe({
      next: (saved) => {
        this.rowsResource.update((rows) =>
          (rows ?? []).map((r) =>
            r.customer.id === row.customer.id ? { ...r, estimate: saved } : r,
          ),
        );
        this.expandedKey.set(null);
        this.draftNote = '';
        this.savingNote.set(false);
      },
      error: () => {
        this.savingNote.set(false);
      },
    });
  }

  // ── Helpers ─────────────────────────────────────────────────────────────────
  private monthBucket(
    value?: string,
  ): { key: string; ts: number; label: string } | null {
    const date = value ? new Date(value) : new Date(NaN);
    if (Number.isNaN(date.getTime())) return null;
    return {
      key: `${date.getFullYear()}-${date.getMonth()}`,
      ts: new Date(date.getFullYear(), date.getMonth(), 1).getTime(),
      label: date.toLocaleDateString('en-US', {
        month: 'short',
        year: 'numeric',
      }),
    };
  }

  private loadRows() {
    return this.api.getCustomers().pipe(
      catchError(() => of([] as Customer[])),
      switchMap((customers) => {
        if (customers.length === 0) {
          return of([] as FinanceRow[]);
        }
        return forkJoin(
          customers.map((c) =>
            this.api.getEstimate(c.id).pipe(catchError(() => of(null))),
          ),
        ).pipe(
          map((estimates) => {
            const rows: FinanceRow[] = [];
            customers.forEach((customer, i) => {
              const estimate = estimates[i];
              if (!estimate) return; // skip customers with no estimate

              const manufacturingCost = estimate.manufacturingCost ?? 0;
              const additionalCosts = (estimate.additionalCosts ?? []).reduce(
                (s, c) => s + (c.amount ?? 0),
                0,
              );
              const profit =
                estimate.total - manufacturingCost - additionalCosts;
              const hasCostData =
                estimate.manufacturingCost != null ||
                (estimate.additionalCosts ?? []).length > 0;
              const paid = (customer.payments ?? []).reduce(
                (s, p) => s + (Number(p.amount) || 0),
                0,
              );
              const balance = estimate.total - paid;

              rows.push({
                customer,
                estimate,
                manufacturingCost,
                additionalCosts,
                profit,
                profitPercent:
                  estimate.total > 0 ? (profit / estimate.total) * 100 : 0,
                hasCostData,
                paid,
                balance,
              });
            });

            // Sort: unprofitable first, then by profit ascending
            rows.sort((a, b) => a.profit - b.profit);
            return rows;
          }),
        );
      }),
    );
  }
}
